import React, { useState } from 'react';
import { getFirstImageUrl } from '../utils/htmlUtils';
import { useLanguage } from '../contexts/LanguageContext';

const ThumbnailPicker = ({ isOpen, onClose, onSelect, contentHtml, currentThumbnail }) => {
  const { t } = useLanguage();
  const [selectedType, setSelectedType] = useState('auto');
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);

  // Initialize state when modal opens or currentThumbnail changes
  React.useEffect(() => {
    if (isOpen) {
      if (currentThumbnail) {
        setSelectedType(currentThumbnail.type || 'auto');
        if (currentThumbnail.type === 'stock') {
          setSelectedThumbnail(currentThumbnail.icon?.id);
        } else if (currentThumbnail.type === 'content') {
          setSelectedThumbnail(currentThumbnail.url);
        } else {
          setSelectedThumbnail(null);
        }
      } else {
        setSelectedType('auto');
        setSelectedThumbnail(null);
      }
    }
  }, [isOpen, currentThumbnail]);

  // Stock icon options
  const stockIcons = [
    { id: 'urgent', icon: '🚨', label: t('thumbnailPicker.stockIcons.urgent'), color: '#ef4444' },
    { id: 'hot', icon: '🔥', label: t('thumbnailPicker.stockIcons.hot'), color: '#f97316' },
    { id: 'new', icon: '✨', label: t('thumbnailPicker.stockIcons.new'), color: '#3b82f6' },
    { id: 'important', icon: '⭐', label: t('thumbnailPicker.stockIcons.important'), color: '#eab308' },
    { id: 'rehearsal', icon: '🎵', label: t('thumbnailPicker.stockIcons.rehearsal'), color: '#8b5cf6' },
    { id: 'performance', icon: '🎭', label: t('thumbnailPicker.stockIcons.performance'), color: '#ec4899' },
    { id: 'event', icon: '📅', label: t('thumbnailPicker.stockIcons.event'), color: '#06b6d4' },
    { id: 'social', icon: '👥', label: t('thumbnailPicker.stockIcons.social'), color: '#10b981' },
    { id: 'announcement', icon: '📢', label: t('thumbnailPicker.stockIcons.announcement'), color: '#f59e0b' },
    { id: 'music', icon: '🎶', label: t('thumbnailPicker.stockIcons.music'), color: '#6366f1' },
    { id: 'celebration', icon: '🎉', label: t('thumbnailPicker.stockIcons.celebration'), color: '#f472b6' },
    { id: 'info', icon: 'ℹ️', label: t('thumbnailPicker.stockIcons.info'), color: '#3b82f6' },
    { id: 'warning', icon: '⚠️', label: t('thumbnailPicker.stockIcons.warning'), color: '#f59e0b' },
    { id: 'success', icon: '✅', label: t('thumbnailPicker.stockIcons.success'), color: '#10b981' },
    { id: 'location', icon: '📍', label: t('thumbnailPicker.stockIcons.location'), color: '#ef4444' },
    { id: 'time', icon: '⏰', label: t('thumbnailPicker.stockIcons.time'), color: '#f97316' }
  ];

  // Extract images from content
  const extractImagesFromContent = (html) => {
    if (!html) return [];
    const images = [];
    const imgRegex = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi;
    let match;
    while ((match = imgRegex.exec(html)) !== null) {
      images.push({
        id: `content-${images.length}`,
        url: match[1],
        type: 'content'
      });
    }
    return images;
  };

  const contentImages = extractImagesFromContent(contentHtml);
  const autoThumbnail = getFirstImageUrl(contentHtml);

  const handleSelect = () => {
    let thumbnail = null;
    
    if (selectedType === 'auto') {
      thumbnail = { type: 'auto', url: autoThumbnail };
    } else if (selectedType === 'stock') {
      const stockIcon = stockIcons.find(icon => icon.id === selectedThumbnail);
      thumbnail = { type: 'stock', icon: stockIcon };
    } else if (selectedType === 'content') {
      const contentImage = contentImages.find(img => img.id === selectedThumbnail);
      thumbnail = { type: 'content', url: contentImage?.url };
    }
    
    onSelect(thumbnail);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: '#1f2937',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
          color: '#ffffff',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            🖼️ {t('thumbnailPicker.title')}
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
          
          {/* Auto Thumbnail */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              border: `2px solid ${selectedType === 'auto' ? '#3b82f6' : '#374151'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              backgroundColor: selectedType === 'auto' ? '#1e3a8a' : '#374151',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="radio"
                name="thumbnailType"
                value="auto"
                checked={selectedType === 'auto'}
                onChange={() => setSelectedType('auto')}
                style={{ margin: 0 }}
              />
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: '#4b5563',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '1px solid #6b7280'
              }}>
                {autoThumbnail ? (
                  <img 
                    src={autoThumbnail}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '20px' }}>📄</span>
                )}
              </div>
              <div>
                <div style={{ color: '#f3f4f6', fontWeight: '600', fontSize: '16px' }}>
{t('thumbnailPicker.autoDetect')}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '14px' }}>
{autoThumbnail ? t('thumbnailPicker.autoDetectDescription') : t('thumbnailPicker.noImagesFound')}
                </div>
              </div>
            </label>
          </div>

          {/* Content Images */}
          {contentImages.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                border: `2px solid ${selectedType === 'content' ? '#3b82f6' : '#374151'}`,
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: selectedType === 'content' ? '#1e3a8a' : '#374151',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="thumbnailType"
                  value="content"
                  checked={selectedType === 'content'}
                  onChange={() => setSelectedType('content')}
                  style={{ margin: 0 }}
                />
                <div style={{ color: '#f3f4f6', fontWeight: '600', fontSize: '16px' }}>
{t('thumbnailPicker.chooseFromContent')} ({contentImages.length})
                </div>
              </label>
              
              {selectedType === 'content' && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: '8px',
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#2d3748',
                  borderRadius: '8px'
                }}>
                  {contentImages.map((image) => (
                    <div
                      key={image.id}
                      onClick={() => setSelectedThumbnail(image.id)}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: selectedThumbnail === image.id ? '3px solid #3b82f6' : '2px solid #4b5563',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <img
                        src={image.url}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stock Icons */}
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              border: `2px solid ${selectedType === 'stock' ? '#3b82f6' : '#374151'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              backgroundColor: selectedType === 'stock' ? '#1e3a8a' : '#374151',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="radio"
                name="thumbnailType"
                value="stock"
                checked={selectedType === 'stock'}
                onChange={() => setSelectedType('stock')}
                style={{ margin: 0 }}
              />
              <div style={{ color: '#f3f4f6', fontWeight: '600', fontSize: '16px' }}>
{t('thumbnailPicker.chooseStockIcon')}
              </div>
            </label>
            
            {selectedType === 'stock' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '8px',
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#2d3748',
                borderRadius: '8px'
              }}>
                {stockIcons.map((iconData) => (
                  <div
                    key={iconData.id}
                    onClick={() => setSelectedThumbnail(iconData.id)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedThumbnail === iconData.id ? iconData.color + '20' : '#374151',
                      border: selectedThumbnail === iconData.id ? `2px solid ${iconData.color}` : '2px solid #4b5563',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{iconData.icon}</span>
                    <span style={{ 
                      fontSize: '11px', 
                      color: '#d1d5db', 
                      textAlign: 'center',
                      lineHeight: '1.2'
                    }}>
                      {iconData.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #374151',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6b7280',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
{t('thumbnailPicker.cancel')}
          </button>
          <button
            onClick={handleSelect}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
{t('thumbnailPicker.selectThumbnail')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThumbnailPicker;